import React, { useEffect, useState } from 'react'
import Button from '@berlitzplatforms/micro.ui.button'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePortalFeatures } from '@components/RoleBasedView'
import { useLazyQuery } from '@apollo/react-hooks'
import { GET_CALENDAR_EVENTS } from '@queries/calendarEvents'
import { IGetCalendarEventsInput, IGetCalendarEventsReponse } from '@interfaces/CalendarEvents'
import { DownloadCalendarBtnWrapper } from './style'
import { DOWNLOAD_CAL_BTN_PROPS } from './constants'
import DownloadCalendarModal from './DownloadCalendarModal'
import useCalendarEvents from '@hooks/useCalendarEvents'
import BasicModal from '@components/Modal'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
import PortalStudentProfile from '@classes/StudentProfile'

interface IDownloadCalendarButton {
  onClick?: () => void
}

interface IBtnProps {
  label: string
  disabled: boolean
  btnType: any
  icon: React.ReactNode
  hasChanges: boolean
}

const DownloadCalendarButton: React.FC<IDownloadCalendarButton> = ({ onClick = () => {} }) => {
  const { state } = usePortalFeatures({
    configId: 'c::myCalendar::downloadCalendar',
  })
  const { is2bStudentOnly } = useSelector(({ userProfile }: StoreProps) => {
    const studentProfile = new PortalStudentProfile(userProfile.info?.StudentProfile?.StudentProfileInfo)
    return {
      is2bStudentOnly: studentProfile?.is2bStudentOnly,
    }
  })
  const [getCalendarEvents, { data = { getCalendarEvents: [] }, loading, called }] = useLazyQuery<
    IGetCalendarEventsReponse,
    IGetCalendarEventsInput
  >(GET_CALENDAR_EVENTS, {
    fetchPolicy: 'no-cache',
  })
  const [btnProps, setBtnProps] = useState<IBtnProps>(DOWNLOAD_CAL_BTN_PROPS.default)
  const [open, setOpen] = useState<boolean>(false)
  const [openInfo, setOpenInfo] = useState(false)
  const [downloaded, setDownloaded] = useState<boolean>(false)
  const { getDownloadButtons, getCalendarEventsPayload, getBtnProps, hasNoSessions } = useCalendarEvents(
    data.getCalendarEvents
  )
  const intl = useIntl()

  useEffect(() => {
    if (state.allowed && !called && !is2bStudentOnly) {
      getCalendarEvents({
        variables: getCalendarEventsPayload(),
      })
    }
  }, [state, called, is2bStudentOnly])

  useEffect(() => {
    if (called && !loading && data?.getCalendarEvents?.length) {
      const props = getBtnProps(downloaded)
      setBtnProps(props)
    }
  }, [loading, called, data, downloaded])

  if (!state.allowed || is2bStudentOnly) {
    return null
  }

  return (
    <>
      <DownloadCalendarBtnWrapper>
        <Button
          onClick={() => {
            if (hasNoSessions() && !data?.getCalendarEvents.length) {
              setOpenInfo(!openInfo)
            } else {
              setOpen(!open)
            }

            onClick()
          }}
          isLoading={loading}
          canLoad
          disabled={btnProps.disabled || (!loading && !data?.getCalendarEvents.length)}
          buttonType={btnProps.btnType}
          className={btnProps.btnType}
        >
          {btnProps.icon}
          <span>&nbsp;</span>
          <FormattedMessage id={btnProps.label} />
        </Button>
      </DownloadCalendarBtnWrapper>

      {!loading && ((data?.getCalendarEvents.length && !hasNoSessions()) || open) ? (
        <DownloadCalendarModal
          open={open}
          onClose={() => {
            setOpen(false)

            let timeout = setTimeout(() => {
              setDownloaded(false)

              return () => clearTimeout(timeout)
            }, 2000)
          }}
          studentCalendar={data?.getCalendarEvents}
          hasChanges={btnProps.hasChanges}
          downloadButtons={getDownloadButtons()}
          onDownloaded={(downloaded) => setDownloaded(downloaded)}
          downloaded={downloaded}
        />
      ) : hasNoSessions() ? (
        <BasicModal
          open={openInfo}
          onClose={() => setOpenInfo(false)}
          type="info"
          modalTitle={intl.formatMessage({
            id: 'No new calendar events to download',
          })}
        ></BasicModal>
      ) : null}
    </>
  )
}

export default DownloadCalendarButton
